import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Field, Input, Button } from "@/components/UI";
import { CurrencyCircleDollar, ChartLine, ClockCounterClockwise, TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { toast } from "sonner";


export function PriceUpdateModal({ item, onClose, onDone }) {
  const [newPrice, setNewPrice] = useState(String(item.unit_price ?? ""));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [trend, setTrend] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/stock/${item.id}/price-history`).then(r => setHistory(r.data.history || [])).catch(() => {}),
      api.get(`/stock/${item.id}/price-trend?months=12`).then(r => setTrend(r.data)).catch(() => {}),
    ]).finally(() => setLoadingHistory(false));
  }, [item.id]);

  const priceNum = Number(newPrice || 0);
  const isSame = Math.abs(priceNum - Number(item.unit_price || 0)) < 1e-9;
  const priceDelta = priceNum - Number(item.unit_price || 0);
  const percentChange = item.unit_price > 0 ? (priceDelta / Number(item.unit_price)) * 100 : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (priceNum <= 0) return toast.error("Geçerli bir fiyat girin");
    if (isSame) return toast.error("Yeni fiyat mevcut fiyatla aynı");
    setSaving(true);
    try {
      const { data } = await api.patch(`/stock/${item.id}/price`, {
        unit_price: priceNum,
        reason: reason || null,
      });
      toast.success(`${item.name} — birim fiyat ${data.old_price.toLocaleString("tr-TR")} ₺ → ${data.new_price.toLocaleString("tr-TR")} ₺ olarak güncellendi`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="price-modal">
      <div className="bg-white rounded-md max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#FEF6E0] rounded-full">
              <CurrencyCircleDollar size={22} weight="duotone" className="text-[#8B6914]" />
            </div>
            <div>
              <h3 className="heading text-xl">Fiyat Güncelle</h3>
              <p className="text-sm text-[#5e675e]">{item.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-2xl text-[#8a938c] hover:text-[#1A1D1A]" data-testid="price-modal-close">×</button>
        </div>

        <div className="bg-[#F7F6F3] border border-[#E5E2DC] rounded-md p-3 mb-4 text-sm">
          <div className="flex justify-between mb-1"><span className="text-[#5e675e]">Mevcut birim fiyat:</span><span className="tabnum font-semibold">{Number(item.unit_price || 0).toLocaleString("tr-TR")} ₺ / {item.unit}</span></div>
          <div className="flex justify-between"><span className="text-[#5e675e]">Ambardaki miktar:</span><span className="tabnum">{Number(item.quantity || 0).toLocaleString("tr-TR")} {item.unit}</span></div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <Field label={`Yeni Birim Fiyat (₺ / ${item.unit}) *`}>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              required
              autoFocus
              data-testid="price-modal-input"
              value={newPrice}
              onChange={e => setNewPrice(e.target.value)}
              placeholder="Örn: 10.50"
            />
          </Field>

          <Field label="Neden (opsiyonel)">
            <Input
              data-testid="price-modal-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Örn: Piyasa fiyatı arttı, Yeni tedarikçi, TL enflasyonu"
            />
          </Field>

          {!isSame && priceNum > 0 && item.unit_price > 0 && (
            <div className={`text-sm px-3 py-2 rounded-md border ${priceDelta > 0 ? "bg-[#fbeee9] border-[#f2d4cb] text-[#8B3A29]" : "bg-[#E4EEE6] border-[#c2d6c8] text-[#2D4A36]"}`} data-testid="price-modal-delta">
              {priceDelta > 0 ? "📈" : "📉"} {priceDelta > 0 ? "+" : ""}{priceDelta.toFixed(2)} ₺ · <b>{percentChange > 0 ? "+" : ""}{percentChange.toFixed(1)}%</b> değişim
            </div>
          )}

          <div className="text-xs text-[#5e675e] bg-[#FEF6E0] border border-[#EED7A6] px-3 py-2 rounded-md" data-testid="price-modal-warning">
            ✋ <b>Önemli:</b> Geçmiş rasyon, ilaç ve sayım kayıtları <b>etkilenmez</b> — bu kayıtlar tüketildikleri anki fiyatta donmuş kalır. Sadece bundan sonraki tüketimler yeni fiyattan hesaplanır.
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>İptal</Button>
            <Button type="submit" disabled={saving || isSame || priceNum <= 0} data-testid="price-modal-submit">
              {saving ? "Kaydediliyor..." : "Fiyatı Güncelle"}
            </Button>
          </div>
        </form>

        {/* 12 Aylık Fiyat Trend Grafiği */}
        {trend && (
          <div className="mt-6 pt-4 border-t border-[#E5E2DC]" data-testid="price-trend-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ChartLine size={16} weight="duotone" className="text-[#5e675e]" />
                <h4 className="heading text-sm">12 Aylık Fiyat Trendi</h4>
              </div>
              {trend.change_count > 0 && (
                <span className={`text-xs font-semibold flex items-center gap-1 ${trend.stats.change_amount > 0 ? "text-[#8B3A29]" : trend.stats.change_amount < 0 ? "text-[#2D4A36]" : "text-[#5e675e]"}`}>
                  {trend.stats.change_amount > 0 ? <TrendUp size={12} weight="bold" /> : trend.stats.change_amount < 0 ? <TrendDown size={12} weight="bold" /> : <Minus size={12} />}
                  {trend.stats.change_pct > 0 ? "+" : ""}{trend.stats.change_pct}%
                </span>
              )}
            </div>
            <div style={{ height: 180 }}>
              <ResponsiveContainer>
                <LineChart data={trend.series} margin={{ top: 10, right: 15, left: 0, bottom: 5 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#8a938c" }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: "#8a938c" }} domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC", fontSize: 12 }}
                    formatter={(v) => [`${Number(v).toLocaleString("tr-TR")} ₺`, "Fiyat"]}
                  />
                  <ReferenceLine y={trend.stats.avg} stroke="#DDA74F" strokeDasharray="3 3" label={{ value: `Ort ${trend.stats.avg}`, position: "insideTopLeft", fill: "#8B6914", fontSize: 10 }} />
                  <Line type="stepAfter" dataKey="price" stroke="#2D4A36" strokeWidth={2.5} dot={{ r: 3, fill: "#2D4A36" }} name="Fiyat" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
              <div className="p-2 bg-[#F7F6F3] rounded" data-testid="trend-min">
                <div className="text-[#8a938c] uppercase tracking-widest text-[9px]">En Düşük</div>
                <div className="font-bold text-[#2D4A36]">{Number(trend.stats.min).toLocaleString("tr-TR")} ₺</div>
              </div>
              <div className="p-2 bg-[#F7F6F3] rounded" data-testid="trend-max">
                <div className="text-[#8a938c] uppercase tracking-widest text-[9px]">En Yüksek</div>
                <div className="font-bold text-[#8B3A29]">{Number(trend.stats.max).toLocaleString("tr-TR")} ₺</div>
              </div>
              <div className="p-2 bg-[#F7F6F3] rounded" data-testid="trend-avg">
                <div className="text-[#8a938c] uppercase tracking-widest text-[9px]">Ortalama</div>
                <div className="font-bold text-[#8B6914]">{Number(trend.stats.avg).toLocaleString("tr-TR")} ₺</div>
              </div>
              <div className="p-2 bg-[#F7F6F3] rounded" data-testid="trend-change">
                <div className="text-[#8a938c] uppercase tracking-widest text-[9px]">Değişim</div>
                <div className={`font-bold ${trend.stats.change_amount > 0 ? "text-[#8B3A29]" : trend.stats.change_amount < 0 ? "text-[#2D4A36]" : "text-[#5e675e]"}`}>
                  {trend.stats.change_amount > 0 ? "+" : ""}{Number(trend.stats.change_amount).toLocaleString("tr-TR")} ₺
                </div>
              </div>
            </div>
            {trend.change_count === 0 && (
              <div className="text-xs text-[#8a938c] mt-2 italic">
                Bu kalem için henüz fiyat değişikliği kaydı yok — sabit fiyat gösteriliyor.
              </div>
            )}
          </div>
        )}

        {/* Fiyat Geçmişi */}
        {(history.length > 0 || loadingHistory) && (
          <div className="mt-6 pt-4 border-t border-[#E5E2DC]">
            <div className="flex items-center gap-2 mb-3">
              <ClockCounterClockwise size={16} weight="duotone" className="text-[#5e675e]" />
              <h4 className="heading text-sm">Fiyat Değişim Geçmişi</h4>
            </div>
            {loadingHistory ? (
              <div className="text-xs text-[#8a938c]">Yükleniyor...</div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="price-history-list">
                {history.slice().reverse().map((h, i) => (
                  <div key={`${h.date}-${i}`} className="text-xs bg-[#F7F6F3] border border-[#EEECE5] rounded p-2">
                    <div className="flex justify-between mb-0.5">
                      <span className="tabnum text-[#5e675e]">{h.date?.slice(0, 16).replace("T", " ")}</span>
                      <span className={`font-semibold ${h.new_price > h.old_price ? "text-[#8B3A29]" : "text-[#2D4A36]"}`}>
                        {Number(h.old_price).toLocaleString("tr-TR")} → {Number(h.new_price).toLocaleString("tr-TR")} ₺
                      </span>
                    </div>
                    <div className="text-[#8a938c]">
                      {h.changed_by_name || h.changed_by}
                      {h.reason && <> · <span className="italic">{h.reason}</span></>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
